import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { Save, Info, Target, Eye, Award } from 'lucide-react';
import SafeJoditEditor from '../components/SafeJoditEditor';
import { editorConfig } from '../utils/joditConfig';
import { useToast } from '../contexts/ToastContext';

interface AboutUsData {
  id?: string;
  title: string;
  subtitle: string;
  description: string;
  mission: string;
  vision: string;
  years_experience: number;
  happy_customers: number;
  projects_completed: number;
  team_members: number;
  featured_image: string | null;
  is_active: boolean;
}

const AboutUsPage: React.FC = () => {
  const { showToast } = useToast();
  const [data, setData] = useState<AboutUsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [selectedGalleryFile, setSelectedGalleryFile] = useState<File | null>(null);
  const editor = useRef(null);

  const fetchAboutData = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedData = await apiService.getAboutUs();
      const aboutData = Array.isArray(fetchedData) ? fetchedData[0] : fetchedData;

      if (aboutData) {
        setData(aboutData);
        setDescription(aboutData.description || '');
        const imageUrl = aboutData.featured_image || aboutData.image;
        setPreviewUrl(getImageUrl(imageUrl));
      } else {
        setData(null);
        setPreviewUrl(null);
      }
    } catch (error) {
      console.error('Failed to fetch about data:', error);
      showToast('Failed to load About Us content.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchGallery = async () => {
    try {
      const data = await apiService.getGallery();
      setGalleryImages(data || []);
    } catch (err) {
      console.error('Failed to fetch gallery:', err);
    }
  };

  useEffect(() => {
    fetchAboutData();
    fetchGallery();
  }, [fetchAboutData]);

  const handleGallerySelect = async (imageUrl: string) => {
    try {
      setPreviewUrl(imageUrl);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const filename = imageUrl.split('/').pop() || 'gallery-image.jpg';
      const file = new File([blob], filename, { type: blob.type });
      setSelectedGalleryFile(file);
    } catch (err) {
      console.error('Failed to prepare gallery image for saving:', err);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setSelectedGalleryFile(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData(e.currentTarget);
    fd.set('description', description);

    // Normalize checkbox
    if (fd.has('is_active')) {
      const val = fd.get('is_active');
      fd.set('is_active', val === 'on' || val === 'true' ? 'true' : 'false');
    } else {
      fd.append('is_active', 'false');
    }

    // Prevent clearing image if no new file selected
    const fileInput = e.currentTarget.querySelector('input[name="featured_image"]') as HTMLInputElement;
    if (fileInput && (!fileInput.files || fileInput.files.length === 0)) {
      if (selectedGalleryFile) {
        fd.set('featured_image', selectedGalleryFile);
      } else {
        fd.delete('featured_image');
      }
    }

    try {
      if (data?.id) {
        await apiService.updateAboutUs(data.id, fd);
      } else {
        await apiService.createAboutUs(fd);
      }
      showToast('About Us content saved successfully!', 'success');
      // Refresh will re-fetch and reset preview to backend URL
      fetchAboutData();
    } catch (error) {
      console.error('Failed to save about us:', error);
      showToast('Error saving about us content.', 'error');
    } finally {
      setSaving(false);
      setSelectedGalleryFile(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">About Us</h1>
        <p className="text-gray-600">Edit company story, mission, vision and site statistics</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* ... Main Content ... */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Info className="h-5 w-5 text-orange-600" /> Main Content
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input name="title" defaultValue={data?.title} required className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500/20 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input name="subtitle" defaultValue={data?.subtitle} className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500/20 outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <SafeJoditEditor
                    ref={editor}
                    value={description}
                    config={editorConfig}
                    onBlur={newContent => setDescription(newContent)}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5"><Target className="h-4 w-4 text-orange-600" /> Mission</label>
                    <textarea name="mission" defaultValue={data?.mission} className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" rows={3} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5"><Eye className="h-4 w-4 text-orange-600" /> Vision</label>
                    <textarea name="vision" defaultValue={data?.vision} className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" rows={3} />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="h-5 w-5 text-orange-600" /> Counter Statistics
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Experience (Yrs)</label>
                  <input type="number" name="years_experience" defaultValue={data?.years_experience || 0} className="w-full border border-gray-200 rounded-lg px-4 py-2 font-bold" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Clients</label>
                  <input type="number" name="happy_customers" defaultValue={data?.happy_customers || 0} className="w-full border border-gray-200 rounded-lg px-4 py-2 font-bold" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Projects</label>
                  <input type="number" name="projects_completed" defaultValue={data?.projects_completed || 0} className="w-full border border-gray-200 rounded-lg px-4 py-2 font-bold" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Team Size</label>
                  <input type="number" name="team_members" defaultValue={data?.team_members || 0} className="w-full border border-gray-200 rounded-lg px-4 py-2 font-bold" />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
              <div className="p-4 border-b border-gray-50 bg-gray-50/50">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Featured Image</h3>
              </div>
              <div className="p-6">
                <div className="mb-4 aspect-video rounded-lg overflow-hidden border border-gray-100 relative group bg-gray-50 flex items-center justify-center">
                  {previewUrl ? (
                    <>
                      <img
                        src={previewUrl}
                        className="w-full h-full object-cover"
                        alt="Preview"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </>
                  ) : (
                    <div className="text-gray-400 flex flex-col items-center gap-1">
                      <Eye className="h-8 w-8 opacity-20" />
                      <span className="text-xs font-medium">No image selected</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  name="featured_image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 transition-colors cursor-pointer"
                />

                {galleryImages.length > 0 && (
                  <div className="mt-6">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Or Choose from Gallery</label>
                    <div className="grid grid-cols-4 gap-2">
                      {galleryImages.map((img, i) => (
                        <div
                          key={i}
                          onClick={() => handleGallerySelect(getImageUrl(img.image) || '')}
                          className={`aspect-square rounded border overflow-hidden cursor-pointer transition-colors ${previewUrl === getImageUrl(img.image) ? 'border-orange-500 ring-2 ring-orange-500/20' : 'border-gray-100 hover:border-orange-300'}`}
                        >
                          <img
                            src={getImageUrl(img.image) || undefined}
                            className="w-full h-full object-cover"
                            alt=""
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2 italic px-1">* Selecting from gallery prepares it for saving. Click Save Changes to apply.</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm font-bold text-gray-700">Section Active</span>
                  <input type="checkbox" name="is_active" defaultChecked={data?.is_active !== false} className="h-5 w-5 text-orange-600 rounded" />
                </div>
                <button type="submit" disabled={saving} className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-bold transition flex items-center justify-center gap-2">
                  {saving ? <div className="h-5 w-5 border-2 border-white/30 border-t-white animate-spin rounded-full" /> : <Save className="h-5 w-5" />}
                  {data?.id ? 'Save Changes' : 'Create Profile'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AboutUsPage;